import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  // Common profanity words list (basic set - can be expanded)
  private profanityWords = [
    'damn', 'hell', 'crap', 'shit', 'fuck', 'ass', 'bitch', 'bastard',
    'piss', 'dick', 'cock', 'pussy', 'asshole', 'motherfucker', 'fucking',
    'shitty', 'damned', 'bloody', 'bugger', 'wanker', 'twat', 'cunt'
  ];

  validateUrl(url: string): { valid: boolean; error?: string } {
    if (!url || url.trim() === '') {
      return { valid: true }; // Empty is valid (optional field)
    }

    try {
      const urlObj = new URL(url);
      // Check for valid protocol
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return { valid: false, error: 'URL must start with http:// or https://' };
      }
      return { valid: true };
    } catch (e) {
      return { valid: false, error: 'Please enter a valid URL (e.g., https://example.com)' };
    }
  }

  validateEmail(email: string): { valid: boolean; error?: string } {
    if (!email || email.trim() === '') {
      return { valid: true }; // Empty is valid (optional field)
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }
    return { valid: true };
  }

  validatePhone(phone: string): { valid: boolean; error?: string } {
    if (!phone || phone.trim() === '') {
      return { valid: true }; // Empty is valid (optional field)
    }

    // Allow formats like +1234567890, (123) 456-7890, 123-456-7890, etc.
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
      return { valid: false, error: 'Please enter a valid phone number (e.g., +1234567890)' };
    }
    return { valid: true };
  }

  validateTextForProfanity(text: string): { valid: boolean; error?: string; foundWords?: string[] } {
    if (!text || text.trim() === '') {
      return { valid: true };
    }

    const textLower = text.toLowerCase();
    const foundWords: string[] = [];

    this.profanityWords.forEach(word => {
      // Use word boundaries to avoid false positives
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      if (regex.test(textLower)) {
        foundWords.push(word);
      }
    });

    if (foundWords.length > 0) {
      return {
        valid: false,
        error: 'Text contains inappropriate language. Please remove offensive content.',
        foundWords: [...new Set(foundWords)]
      };
    }

    return { valid: true };
  }

  validateRequired(value: any, fieldName: string): { valid: boolean; error?: string } {
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
      return { valid: false, error: `${fieldName} is required` };
    }
    return { valid: true };
  }

  validateLatitude(lat: number | undefined): { valid: boolean; error?: string } {
    if (lat === undefined || lat === null) {
      return { valid: true }; // Optional
    }
    if (isNaN(lat) || lat < -90 || lat > 90) {
      return { valid: false, error: 'Latitude must be between -90 and 90' };
    }
    return { valid: true };
  }

  validateLongitude(lng: number | undefined): { valid: boolean; error?: string } {
    if (lng === undefined || lng === null) {
      return { valid: true }; // Optional
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      return { valid: false, error: 'Longitude must be between -180 and 180' };
    }
    return { valid: true };
  }

  validateDateTime(dateTime: string | undefined): { valid: boolean; error?: string } {
    if (!dateTime || dateTime.trim() === '') {
      return { valid: true }; // Optional
    }
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) {
      return { valid: false, error: 'Please enter a valid date and time' };
    }
    return { valid: true };
  }
}

